import { useRouter } from "next/router";

import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { GetServerSideProps, NextPage } from "next";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";

import Payment from "modules/Payment";
import useCart from "modules/Cart/useCart";
import { PaymentContext } from "modules/Payment/paymentContext";

import { selectChainId } from "redux/slices/metamask";
import { generateHash } from "redux/slices/logger";

import useCryptoPayment from "sections/storesPage/useCryptoPayment";
import Requirements from "sections/storesPage/Requirements";
import ShippingInfo from "sections/storesPage/ShippingInfo";
import Attributes from "sections/itemsPage/SectionAttributes";
import AvailableAmount from "sections/storesPage/AvailableAmount";
import useFiatPayment from "sections/storesPage/useFiatPayment";
import useCancelPayment from "sections/storesPage/useCancelPayment";

import CustomCheckbox from "components/CustomCheckbox";
import CustomLink from "components/CustomLink";
import ItemModel from "components/ItemModel";
import BoxLabel from "components/BoxLabel";
import ClientOnly from "components/ClientOnly";
import CustomButton from "components/CustomButton";
import Layout from "components/Layout";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Divider from "components/Divider";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";

import useOpenseaLink from "hooks/useOpenseaLink";

import { PAYMENT_FIATS } from "enums/paymentTokens";
import { STORE_VERSIONS } from "enums/storeTypes";
import { PRODUCT_TYPES } from "enums/productTypes";

import { ClientStudioBundleFull } from "interfaces/ClientStudio.interface";

import { API_ROUTES, APP_ROUTES } from "config/routes";

import { isAddressesEquals, getEthereumChainId } from "utils/blockchain";
import { dateToUnixTime, formatUTCSeconds } from "utils/date";
import {
  getAvailableAndTotalAmount,
  getMaxAmount,
  getSubdomain,
} from "utils/store";
import { mapURLSearchParams } from "utils/query";

import { Client } from "services/store/Store.interface";
import { SaleInfoInterface } from "services/store/sale/saleInfo";
import BundlePrice from "services/store/bundling/BundlePrice";

import { withSessionSsr } from "lib/session";
import fetcher from "lib/fetchJson";

interface StoreBundlePageProps {
  data: ClientStudioBundleFull & {
    fiatPrice: number;
    metadataType: PRODUCT_TYPES | string;
    refToken: PAYMENT_FIATS;
    saleInfo: SaleInfoInterface;
    client: Client;
  };
  queryChainId: number | null;
  mintChainId: number;
  isEmbedded: boolean;
}

const StoreBundlePage: NextPage<StoreBundlePageProps> = ({
  data: bundle,
  queryChainId,
  isEmbedded,
  mintChainId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { addMintItemToCart } = useCart();
  const { query, replace, pathname } = useRouter();

  const chainId = useSelector(selectChainId);

  const [isConsentChecked, setConsentChecked] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const {
    id,
    name,
    description,
    image,
    metadata,
    paymentDetails,
    client,
    requirements,
    constraints: bundleConstraints,
    refToken,
    minShipmentDays,
    maxShipmentDays,
    saleInfo,
    refPrice,
    metadataType,
    model,
  } = bundle;
  const {
    isSaleFinished,
    isStoreOnSale,
    isBundleOnSale,
    isSoldOut,
    isFree: isFreeMint,
  } = saleInfo;
  const constraints = bundleConstraints || client.constraints;
  const { availableAmount, totalAmount } = getAvailableAndTotalAmount(bundle);
  const isSelling =
    isStoreOnSale &&
    isBundleOnSale &&
    !isSaleFinished &&
    chainId === queryChainId;

  const { openseaUrl } = useOpenseaLink({
    collectionName: client.name,
    chainIds: client.chainIds,
    bundleName: name,
    isCreatedByForm: !!client.isCreatedByForm,
    contractAddress: client.mintContractAddress,
  });

  const handleCancel = useCancelPayment();
  const handleFiatPayment = useFiatPayment(id);
  const handleCryptoPayment = useCryptoPayment(
    id,
    client.mintContractAddress,
    client.mintType,
  );

  const onConsentChange = useCallback((e) => {
    setConsentChecked(e.target.checked);
  }, []);

  const handleMintNow = useCallback(() => {
    setShowPayment(true);
  }, []);

  const handleAddToCart = useCallback(() => {
    addMintItemToCart(id);
    setShowPayment(false);
  }, [addMintItemToCart, id]);

  const handleFiatPrice = useCallback(
    (amount: number) => {
      return BundlePrice.calculateRefPrice(
        Number.isFinite(refPrice) ? Number(refPrice) : null,
        bundle.client,
        metadataType,
        true,
        bundle.model?.printsNumber ?? undefined,
        amount,
      );
    },
    [refPrice, bundle.client, metadataType, bundle.model?.printsNumber],
  );

  useEffect(() => {
    if (chainId && chainId !== queryChainId) {
      replace({
        pathname,
        query: {
          ...query,
          chainId,
        },
      });
    }
  }, [chainId, queryChainId, pathname, replace, query]);

  useEffect(() => {
    dispatch(generateHash());
  }, [dispatch]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY as string}
    >
      <Layout isEmbedded={isEmbedded}>
        <GridContainer spacing={4}>
          <GridItem xs={12} md={6} lg={5}>
            <ItemModel
              image={image || ""}
              frontImage={metadata.data?.front}
              backImage={metadata.data?.back}
              modelHash={metadata.modelHash}
              bgColor={model?.bgColor ?? undefined}
            >
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
                <BoxLabel show={isSoldOut}>SOLD OUT</BoxLabel>
                <BoxLabel show={!isStoreOnSale && !!client.startSellingDate}>
                  <b>
                    SALE STARTS ON{" "}
                    {formatUTCSeconds(
                      dateToUnixTime(client.startSellingDate ?? 0) / 1000,
                    )}
                  </b>
                </BoxLabel>
              </Box>
            </ItemModel>
            <Box sx={{ display: { xs: "none", lg: "block" }, mt: 2 }}>
              <Attributes attributes={metadata?.attributes || null} />
            </Box>
          </GridItem>
          <GridItem xs={12} md={6} lg={7}>
            <ClientOnly>
              {isSelling && availableAmount !== 0 && totalAmount !== 0 ? (
                <AvailableAmount
                  availableAmount={availableAmount}
                  totalAmount={totalAmount}
                />
              ) : null}
            </ClientOnly>
            <Typography
              component="h2"
              variant="h2"
              gutterBottom
              sx={{ textAlign: { xs: "left" } }}
            >
              {name}
            </Typography>

            <Typography paragraph sx={{ whiteSpace: "pre-line" }}>
              {description}
            </Typography>

            <Box mt={2} sx={{ display: { xs: "block", lg: "none" } }}>
              <Divider />
              <Attributes attributes={metadata?.attributes || null} />
            </Box>
            <Divider />
            <Requirements
              singleItemView
              requirements={requirements}
              constraints={constraints}
            />
            {client.storeType !== STORE_VERSIONS.PHYSICAL && (
              <>
                <Divider />
                <ShippingInfo
                  storeType={client.storeType}
                  minShipmentDays={minShipmentDays}
                  maxShipmentDays={maxShipmentDays}
                />
              </>
            )}

            <Divider />

            <Box>
              {(isSoldOut || isSaleFinished) && (
                <CustomButton
                  href={openseaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  BUY ON MARKETPLACE
                </CustomButton>
              )}
              {!isSoldOut && !isSaleFinished && (
                <Box sx={{ my: 2 }}>
                  <CustomButton
                    sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 } }}
                    minWidth={!isMobile}
                    fullWidth={isMobile}
                    onClick={handleMintNow}
                    disabled={!isStoreOnSale}
                  >
                    Mint now
                  </CustomButton>
                  <CustomButton
                    minWidth={!isMobile}
                    fullWidth={isMobile}
                    onClick={handleAddToCart}
                    disabled={!isStoreOnSale}
                  >
                    Add to cart
                  </CustomButton>
                </Box>
              )}
              <Collapse in={showPayment}>
                <PaymentContext.Provider
                  value={{
                    buttonText: "Buy",
                    modalType: "STORE",
                    bundleId: id,
                    chainIds: client.chainIds,
                    // ToDo Just to enable fiats and mint on ETH for The Polacy & chibi dinos
                    mintChainId:
                      isAddressesEquals(
                        client.mintContractAddress,
                        "0xdF02c9C1EfC01E8Fd1657A5E0403508eC6514791",
                      ) ||
                      isAddressesEquals(
                        client.mintContractAddress,
                        "0xdcF70e9f4cB41476b65C49ebDAC926EdBac599B9",
                      )
                        ? null
                        : mintChainId,
                    isFreeMint,
                    fiatPrice: handleFiatPrice,
                    paymentDetails,
                    buyDisabled: !isSelling || !isConsentChecked,
                    availableAmount: getMaxAmount(constraints, availableAmount),
                    uniqDiscount: null,
                    fiatCurrency: refToken ?? PAYMENT_FIATS.USD,
                  }}
                >
                  {!isSoldOut && !isSaleFinished && (
                    <Payment
                      showCurrency={!isFreeMint}
                      showQuantity={!isFreeMint}
                      showNetworks={!isFreeMint}
                      showPaymentMethods={!isFreeMint}
                      onFiatPayment={handleFiatPayment}
                      onCryptoPayment={handleCryptoPayment}
                      onFiatCancel={handleCancel}
                    >
                      <>
                        {!isStoreOnSale && client.startSellingDate && (
                          <Box mt={2}>
                            <InfoBox type={INFO_BOX_TYPE.INFO}>
                              Sale starts on{" "}
                              {formatUTCSeconds(
                                dateToUnixTime(client.startSellingDate) / 1000,
                              )}
                            </InfoBox>
                          </Box>
                        )}
                        {isFreeMint &&
                          isBundleOnSale &&
                          isStoreOnSale &&
                          !(isSoldOut || isSaleFinished) && (
                            <Box mt={2}>
                              <InfoBox type={INFO_BOX_TYPE.INFO}>
                                This transaction is currently free. It's a
                                time-limited promotion, take it now!
                              </InfoBox>
                            </Box>
                          )}
                        <FormControlLabel
                          value="end"
                          checked={isConsentChecked}
                          control={<CustomCheckbox />}
                          sx={{ mt: 2, mb: 1 }}
                          label={
                            <Typography
                              component="span"
                              variant="subtitle1"
                              fontWeight="light"
                            >
                              I have read and accept{" "}
                              <CustomLink
                                href={APP_ROUTES.TERMS}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Terms & Conditions
                              </CustomLink>
                              {" and "}
                              <CustomLink
                                href={APP_ROUTES.PRIVACY}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Privacy Policy
                              </CustomLink>
                              *
                            </Typography>
                          }
                          labelPlacement="end"
                          onChange={onConsentChange}
                        />
                        <Typography variant="body2">
                          *Required consents / fields
                        </Typography>
                      </>
                    </Payment>
                  )}
                </PaymentContext.Provider>
              </Collapse>
            </Box>
          </GridItem>
        </GridContainer>
      </Layout>
    </GoogleReCaptchaProvider>
  );
};

export const getServerSideProps: GetServerSideProps = withSessionSsr(
  async (ctx) => {
    const isEmbedded =
      !!getSubdomain(ctx.req.headers.host as string) || !!ctx.query?.embedded;

    const [id] = ctx.query.params as string[];
    const { chainId, token } = ctx.query;
    try {
      if (!Number.isInteger(parseInt(id))) {
        throw new Error(`Given ID "${id}" is not a number!`);
      }
      let headers: { "Content-Type": string; cookie?: string } = {
        "Content-Type": "application/json",
      };
      if (ctx.req) {
        headers = { ...headers, cookie: ctx.req.headers.cookie };
      }

      const params = mapURLSearchParams({
        chainId: (chainId as string) ?? getEthereumChainId(),
        ...(token ? { token: token as string } : {}),
      });
      const { data } = await fetcher(
        `${process.env.NEXT_PUBLIC_APP_URL}${API_ROUTES.STORE_BUNDLES}/${id}?${params}`,
        null,
        "GET",
        headers,
      );

      if (data.client.isCreatedByForm && !data.client.isLive) {
        throw new Error("Store is not public yet!");
      }

      const { mintChainId, ...rest } = data;

      return {
        props: {
          data: rest,
          mintChainId,
          queryChainId: chainId ? parseInt(chainId as string) : null,
          isEmbedded,
        },
      };
    } catch {
      return {
        redirect: {
          destination: "/404",
          permanent: false,
        },
      };
    }
  },
);

export default StoreBundlePage;
